import { type FormEvent, type ReactNode, useEffect, useRef, useState } from "react";
import { LockKeyhole, LogOut, ShieldCheck } from "lucide-react";

import {
  cancelMfaStepUp,
  completeMfaStepUp,
  notifySessionExpired,
  subscribeMfaStepUp,
  subscribeSessionExpired,
} from "./mfaStepUp";
import {
  topmostPlatformLayerRoot,
  usePlatformLayer,
} from "../../ui/useDismissibleLayer";
import { useAuthCopy } from "./authCatalog";

const API_BASE = import.meta.env.VITE_API_BASE ?? "/api/v1";

function cookie(name: string): string | undefined {
  return document.cookie.split(";").map((item) => item.trim())
    .find((item) => item.startsWith(`${name}=`))?.slice(name.length + 1);
}

function focusable(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(
    "button:not([disabled]), input:not([disabled]), [href], [tabindex]:not([tabindex='-1'])",
  ));
}

export function MfaStepUpBoundary({
  children,
  onSessionExpired,
}: {
  children: ReactNode;
  onSessionExpired: () => void;
}) {
  const { t } = useAuthCopy();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [recoveryMode, setRecoveryMode] = useState(false);
  const dialogRef = usePlatformLayer<HTMLDivElement>(
    open,
    () => undefined,
    {
      dismissOnEscape: false,
      dismissOnOutside: false,
      restoreFocus: false,
    },
  );
  const openerRef = useRef<HTMLElement | null>(null);
  const openerDialogRef = useRef<HTMLElement | null>(null);
  const restoreFocusPendingRef = useRef(false);

  useEffect(() => subscribeMfaStepUp((next) => {
    if (next) {
      const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      const underlyingLayer = topmostPlatformLayerRoot();
      const underlyingModal = underlyingLayer?.matches("[role='dialog'][aria-modal='true']")
        ? underlyingLayer
        : null;
      openerRef.current = opener;
      openerDialogRef.current = opener?.closest<HTMLElement>("[role='dialog'][aria-modal='true']")
        ?? underlyingModal;
    }
    if (!next) {
      setBusy(false);
      setError(null);
      setRecoveryMode(false);
      restoreFocusPendingRef.current = true;
    }
    setOpen(next);
  }), []);

  useEffect(() => {
    if (open || !restoreFocusPendingRef.current) return;
    restoreFocusPendingRef.current = false;

    const opener = openerRef.current;
    const fallbackDialog = openerDialogRef.current;
    const restore = () => {
      const openerUnavailable = !opener
        || !opener.isConnected
        || opener === document.body
        || opener === document.documentElement
        || opener.closest("[inert]") !== null
        || opener.getAttribute("aria-disabled") === "true"
        || (
          (opener instanceof HTMLButtonElement
            || opener instanceof HTMLInputElement
            || opener instanceof HTMLSelectElement
            || opener instanceof HTMLTextAreaElement)
          && opener.disabled
        );
      if (!openerUnavailable) {
        opener.focus();
        return;
      }
      if (fallbackDialog?.isConnected && fallbackDialog.closest("[inert]") === null) {
        fallbackDialog.focus();
      }
    };

    if (typeof window.requestAnimationFrame === "function") {
      const frame = window.requestAnimationFrame(restore);
      return () => window.cancelAnimationFrame(frame);
    }
    const timer = window.setTimeout(restore, 0);
    return () => window.clearTimeout(timer);
  }, [open]);

  useEffect(() => subscribeSessionExpired(onSessionExpired), [onSessionExpired]);

  useEffect(() => {
    if (!open) return;
    const dialog = dialogRef.current;
    const input = dialog?.querySelector<HTMLInputElement>("input");
    input?.focus();
    const keydown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      if (event.key !== "Tab" || !dialog) return;
      const items = focusable(dialog);
      if (!items.length) return;
      const first = items[0]!;
      const last = items.at(-1)!;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", keydown, true);
    return () => document.removeEventListener("keydown", keydown, true);
  }, [open]);

  async function verify(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const code = String(new FormData(form).get("code") ?? "").trim();
    setBusy(true);
    setError(null);
    const csrf = cookie("kavoosh_csrf");
    try {
      const response = await fetch(`${API_BASE}/auth/step-up`, {
        method: "POST",
        credentials: "include",
        cache: "no-store",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          ...(csrf ? { "X-CSRF-Token": decodeURIComponent(csrf) } : {}),
        },
        body: JSON.stringify({ code }),
      });
      if (response.status === 401) {
        const body = await response.json().catch(() => ({})) as { detail?: unknown };
        if (typeof body.detail === "object" && body.detail !== null
          && "code" in body.detail && body.detail.code === "AUTH_SESSION_EXPIRED") {
          notifySessionExpired();
          return;
        }
      }
      if (!response.ok) {
        setError(recoveryMode
          ? t("mfa.recoveryInvalid")
          : t("mfa.authInvalid"));
        return;
      }
      form.reset();
      completeMfaStepUp();
    } catch {
      setError(t("mfa.connectionError"));
    } finally {
      setBusy(false);
    }
  }

  async function signOut() {
    setBusy(true);
    const csrf = cookie("kavoosh_csrf");
    await fetch(`${API_BASE}/auth/logout`, {
      method: "POST",
      credentials: "include",
      headers: csrf ? { "X-CSRF-Token": decodeURIComponent(csrf) } : {},
    }).catch(() => undefined);
    cancelMfaStepUp(t("mfa.signOut"));
    notifySessionExpired();
  }

  return <>
    <div className="kavoosh-authenticated-surface" inert={open}>{children}</div>
    {open && <div className="kavoosh-mfa-backdrop" onMouseDown={(event) => event.preventDefault()}>
      <div
        ref={dialogRef}
        className="kavoosh-mfa-dialog"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="mfa-step-up-title"
        aria-describedby="mfa-step-up-description"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <span className="kavoosh-mfa-icon"><ShieldCheck size={24} /></span>
        <p className="admin-v12-eyebrow">{t("mfa.eyebrow")}</p>
        <h2 id="mfa-step-up-title">{t("mfa.title")}</h2>
        <p id="mfa-step-up-description">{recoveryMode
          ? t("mfa.recoveryBody")
          : t("mfa.authBody")}</p>
        <form onSubmit={(event) => void verify(event)}>
          <label>{recoveryMode ? t("mfa.recoveryLabel") : t("mfa.authLabel")}
            <input
              name="code"
              required
              minLength={6}
              maxLength={64}
              inputMode={recoveryMode ? "text" : "numeric"}
              autoComplete="one-time-code"
              pattern={recoveryMode ? undefined : "[0-9]{6}"}
            />
          </label>
          {error && <p className="admin-v12-form-error" role="alert">{error}</p>}
          <button className="admin-v12-primary" disabled={busy}>{busy ? t("mfa.verifying") : t("mfa.verify")}</button>
        </form>
        <div className="kavoosh-mfa-secondary-actions">
          <button type="button" onClick={() => { setRecoveryMode((value) => !value); setError(null); }}>
            <LockKeyhole size={15} />{recoveryMode ? t("mfa.useAuthenticator") : t("mfa.useRecovery")}
          </button>
          <button type="button" disabled={busy} onClick={() => void signOut()}>
            <LogOut size={15} />{t("mfa.signOut")}
          </button>
        </div>
      </div>
    </div>}
  </>;
}
